import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ArrowRight,
  ChevronDown,
  FileText,
  ReceiptText,
  UserPlus,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { useMemo } from 'react';
import { MenuLink } from '@/components/menu-link';
import { EmptyState, PageHeader } from '@/components/page-header';
import { InvoiceMoneyBar } from '@/components/sales/invoice-money-bar';
import { SalesTable } from '@/components/sales/sales-table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useMe } from '@/lib/clients';
import { useEInvoiceMap } from '@/lib/einvoicing';
import { openNewDrawer } from '@/lib/new-drawer';
import { useOrgId } from '@/lib/org';
import { useSalesTransactions, type SalesRow } from '@/lib/sales';

export const Route = createFileRoute('/sales/')({ component: SalesOverview });

/** How many recent transactions the overview lists before "See all sales". */
export const RECENT_LIMIT = 10;

/** The latest sales transactions, newest first (ties by number, as the lists sort them). */
export function recentSales(rows: SalesRow[], limit = RECENT_LIMIT): SalesRow[] {
  return [...rows]
    .sort((a, b) => b.date.localeCompare(a.date) || (b.number ?? '').localeCompare(a.number ?? ''))
    .slice(0, limit);
}

interface Shortcut {
  label: string;
  icon: LucideIcon;
  to?: string;
  drawer?: 'customer';
  testId: string;
}

const SHORTCUTS: Shortcut[] = [
  { label: 'New invoice', icon: FileText, to: '/sales/invoices/new', testId: 'shortcut-invoice' },
  {
    label: 'Receive payment',
    icon: Wallet,
    to: '/sales/receive-payment/new',
    testId: 'shortcut-payment',
  },
  { label: 'New quote', icon: ReceiptText, to: '/sales/quotes/new', testId: 'shortcut-quote' },
  { label: 'New customer', icon: UserPlus, drawer: 'customer', testId: 'shortcut-customer' },
];

/** Sales › Overview (document 06 S1): money bar, shortcuts and the latest sales transactions. */
function SalesOverview() {
  const orgId = useOrgId();
  const me = useMe();
  const navigate = useNavigate();
  const tx = useSalesTransactions();
  const einvoices = useEInvoiceMap();
  const recent = useMemo(() => recentSales(tx.data ?? []), [tx.data]);
  const isStaff = me.data?.isStaff ?? false;

  return (
    <>
      <PageHeader
        title="Sales"
        description="Overview"
        actions={
          isStaff ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="gap-1">
                  New transaction <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <MenuLink to="/sales/invoices/new">Invoice</MenuLink>
                <MenuLink to="/sales/receive-payment/new">Payment</MenuLink>
                <MenuLink to="/sales/quotes/new">Quote</MenuLink>
                <MenuLink to="/sales/credit-notes/new">Credit note</MenuLink>
                <MenuLink to="/sales/sales-receipts/new">Sales receipt</MenuLink>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null
        }
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : (
        <>
          <InvoiceMoneyBar
            onSelect={(key) => {
              if (!key) return;
              void navigate({
                to: '/sales/invoices',
                search: { status: key === 'overdue' || key === 'not_due' ? key : 'paid' },
              });
            }}
          />
          {isStaff ? (
            <section aria-label="Shortcuts" className="mb-4" data-testid="sales-shortcuts">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {SHORTCUTS.map((s) =>
                  s.drawer ? (
                    <button
                      key={s.label}
                      type="button"
                      data-testid={s.testId}
                      onClick={() => openNewDrawer({ kind: s.drawer! })}
                      className="flex items-center gap-3 rounded-lg border bg-card px-4 py-3 text-left text-sm font-medium transition hover:border-brand-blue-200 hover:bg-accent hover:text-accent-foreground"
                    >
                      <ShortcutIcon icon={s.icon} />
                      {s.label}
                    </button>
                  ) : (
                    <Link
                      key={s.label}
                      to={s.to as never}
                      data-testid={s.testId}
                      className="flex items-center gap-3 rounded-lg border bg-card px-4 py-3 text-sm font-medium transition hover:border-brand-blue-200 hover:bg-accent hover:text-accent-foreground"
                    >
                      <ShortcutIcon icon={s.icon} />
                      {s.label}
                    </Link>
                  ),
                )}
              </div>
            </section>
          ) : null}
          <section aria-labelledby="recent-sales" className="rounded-lg border bg-card">
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <h2 id="recent-sales" className="text-base font-semibold">
                Recent sales
              </h2>
              <Link to="/sales/all" className="flex items-center gap-1 text-sm text-primary">
                See all sales <ArrowRight className="size-3.5" />
              </Link>
            </div>
            {tx.isPending ? (
              <p className="px-4 pb-4 text-sm text-muted-foreground">Loading…</p>
            ) : tx.isError ? (
              <p className="px-4 pb-4 text-sm text-destructive">{tx.error.message}</p>
            ) : !recent.length ? (
              <EmptyState
                title="No sales yet"
                hint={
                  isStaff
                    ? 'Create an invoice, quote or sales receipt to see it here.'
                    : 'Invoices and payments will appear here.'
                }
              />
            ) : (
              <SalesTable rows={recent} einvoices={einvoices} />
            )}
          </section>
        </>
      )}
    </>
  );
}

function ShortcutIcon({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <span className="grid size-8 place-items-center rounded bg-brand-blue-50">
      <Icon className="size-4 text-primary" />
    </span>
  );
}
